import { jwtDecode } from "jwt-decode";
import { OPEN_PAGES_IN_SAME_TAB_CLAIM } from "../auth/featureScopes";
import { tokenStorageKey } from "../config";

type NavigateFn = (path: string) => unknown;

let navigate: NavigateFn | undefined;

/** Called once from index.tsx with the router's navigate so same-tab moves stay in the SPA. */
export function registerSameTabNavigate(fn: NavigateFn) {
  navigate = fn;
}

/** Returns false when no router is registered, so the caller can fall back to a new tab. */
export function navigateInApp(path: string): boolean {
  if (!navigate) return false;
  navigate(path);
  return true;
}

// Read from the stored token at click time rather than AuthContext: openInNewTab is a plain
// function called from dozens of components, and this always sees the latest token.
export function opensPagesInSameTab(): boolean {
  try {
    const token = localStorage.getItem(tokenStorageKey);
    if (!token) return false;
    const payload = jwtDecode<Record<string, unknown>>(token);
    return payload[OPEN_PAGES_IN_SAME_TAB_CLAIM] === "true";
  } catch {
    return false;
  }
}

/**
 * The in-app path for `url`, or null when it points at another site. Many callers pass
 * "leads/123" without a leading slash, so relative paths resolve from the root.
 */
export function sameTabPath(url: string, origin: string): string | null {
  try {
    const resolved = new URL(url, `${origin}/`);
    if (resolved.origin !== origin) return null;
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
}
